/**
 * Where the live timeline's displayed window ends — which is deliberately NOT the playhead.
 *
 * While the timeline is following the live edge the two are the same thing: the window ends at the
 * edge and slides forward with it. While paused they have to come apart. If the window keeps ending
 * at the playhead, every scrub re-bases the whole frame so the clicked instant becomes the
 * right-hand edge — the chart appears to run away from the pointer instead of a playhead being
 * placed in it.
 *
 * So a paused window is FROZEN where following stopped, and the playhead moves inside it. The one
 * exception is a seek that would leave the frame altogether — an alarm click can land well outside
 * the current window, and a playhead outside the scale is simply not drawn — so that re-anchors and
 * brings the frame to it.
 */
export interface LiveWindowAnchorInput {
  /** Is the timeline chasing the live data edge? */
  following: boolean;
  /** Where the playhead is now. */
  playheadMs: number;
  /** Width of the displayed window, in minutes. */
  windowMinutes: number;
  /** The anchor currently in force, or null if there is none yet / we were following. */
  currentAnchorMs: number | null;
}

/**
 * @returns the end of the window to display, or `null` while following (the caller then uses the
 *          live playhead directly, so the window keeps sliding).
 */
export function resolveLiveWindowAnchorMs(input: LiveWindowAnchorInput): number | null {
  const { following, playheadMs, windowMinutes, currentAnchorMs } = input;

  // Following: no anchor at all, so resuming always snaps back to a live-edge window.
  if (following) return null;

  if (!Number.isFinite(playheadMs)) return currentAnchorMs;

  // First frame after the pause: freeze here.
  if (currentAnchorMs == null) return playheadMs;

  // The window is (anchor - span, anchor]. Only a playhead that has left it moves the frame.
  const spanMs = Math.max(1, windowMinutes) * 60 * 1000;
  const insideFrame = playheadMs <= currentAnchorMs && playheadMs >= currentAnchorMs - spanMs;

  return insideFrame ? currentAnchorMs : playheadMs;
}
